import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, Tooltip,
  Snackbar, Alert, Avatar, CircularProgress, Divider, Drawer, Tabs, Tab,
  TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Collapse,
  LinearProgress, List, ListItem, ListItemIcon, ListItemText, Grid,
  MenuItem, Select, FormControl,
} from '@mui/material';
import {
  DataGrid, GridColDef, GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  CalendarMonth, Apple, Android, Download, Cancel, Error as ErrorIcon,
  OpenInNew, Close as CloseIcon, ContentCopy, Search as SearchIcon,
  Clear as ClearIcon, GroupWork, ViewList, Timer, Terminal, Refresh,
  OpenInFull, CloseFullscreen,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';

interface PipelineError {
  id: string;
  pipelineId: string;
  appName: string;
  submitter: string;
  timestamp: string;
  status: string;
  phase?: string;
  errorType: 'failed' | 'timeout' | 'stuck' | 'cancelled';
  errorDescription: string;
  errorCode?: string | null;
  platform?: string;
  version?: string;
  pipelineUrl?: string;
}

interface ErrorLogsComponentProps { isActive: boolean }

export const ErrorLogsComponent: React.FC<ErrorLogsComponentProps> = ({ isActive }) => {
  const { dashboardData, fetchDashboardData, isLoadingAppData } = useAuth();

  const [pipelineErrors, setPipelineErrors] = useState<PipelineError[]>([]);
  const [allPipelineErrors, setAllPipelineErrors] = useState<PipelineError[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Search + grouping + drawer state
  const [errorSearch, setErrorSearch] = useState('');
  const [tableView, setTableView] = useState<'list' | 'grouped'>('list');
  const [drawerError, setDrawerError] = useState<PipelineError | null>(null);
  const [drawerTab, setDrawerTab] = useState<0 | 1 | 2>(0);
  const [drawerPhases, setDrawerPhases] = useState<any[] | null>(null);
  const [drawerPhasesLoading, setDrawerPhasesLoading] = useState(false);
  const [drawerPhasesError, setDrawerPhasesError] = useState<string | null>(null);
  // Inline GitLab job-trace state. The Log tab fetches lazily — only when the
  // user actually clicks the tab — so we don't waste a multi-MB request on
  // every drawer open.
  type DrawerJob = { id: number; name: string; status: string; web_url: string };
  const [drawerJobs, setDrawerJobs] = useState<DrawerJob[] | null>(null);
  const [drawerJobId, setDrawerJobId] = useState<number | null>(null);
  const [drawerTrace, setDrawerTrace] = useState<string>('');
  const [drawerTraceMeta, setDrawerTraceMeta] = useState<{ truncated: boolean; totalBytes: number; returnedBytes: number; job?: DrawerJob } | null>(null);
  const [drawerTraceLoading, setDrawerTraceLoading] = useState(false);
  const [drawerTraceError, setDrawerTraceError] = useState<string | null>(null);

  // -------------------- Resizable drawer --------------------
  // The drawer hosts logs / phase timings / diagnostics, all of which scale
  // poorly at 540 px when an operator wants to read a long stack trace or
  // see every phase row at once. We let users drag the drawer's left edge
  // to widen it, or one-click expand to 95vw, and remember the chosen width
  // across sessions so the same operator doesn't keep re-resizing.
  const DRAWER_DEFAULT_WIDTH = 540;
  const DRAWER_MIN_WIDTH = 380;
  const DRAWER_STORAGE_KEY = 'errorLogsDrawerWidth:v1';

  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(DRAWER_STORAGE_KEY);
      if (stored) {
        const n = parseInt(stored, 10);
        if (Number.isFinite(n) && n >= DRAWER_MIN_WIDTH) return n;
      }
    } catch { /* ignore — fall through to default */ }
    return DRAWER_DEFAULT_WIDTH;
  });
  const [drawerExpanded, setDrawerExpanded] = useState(false);

  // Keep the last user-resized width separately so toggling Expand/Restore
  // returns to whatever the operator dragged to, not the default.
  const drawerWidthRef = useRef(drawerWidth);
  useEffect(() => { drawerWidthRef.current = drawerWidth; }, [drawerWidth]);
  const isResizingRef = useRef(false);
  // Monotonic id for the in-flight trace fetch. Switching jobs fast can overlap
  // requests; only the newest is allowed to commit so a slow earlier response
  // can't overwrite the log for the current selection.
  const traceSeqRef = useRef(0);

  // Drag handle on the drawer's left edge. The drawer is anchored right, so
  // the resulting width = viewport width minus the cursor's x position.
  const startDrawerResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setDrawerExpanded(false);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const maxW = Math.floor(window.innerWidth * 0.95);
      const next = Math.min(maxW, Math.max(DRAWER_MIN_WIDTH, window.innerWidth - e.clientX));
      setDrawerWidth(next);
    };
    const onUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist the dragged width so reopening the drawer (or coming back
      // tomorrow) lands on the same size.
      try { localStorage.setItem(DRAWER_STORAGE_KEY, String(drawerWidthRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Effective rendered width — Expand jumps to 95vw without overwriting the
  // user's saved drag preference.
  const [viewportWidth, setViewportWidth] = useState<number>(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const effectiveDrawerWidth = drawerExpanded
    ? Math.floor(viewportWidth * 0.95)
    : Math.min(drawerWidth, Math.floor(viewportWidth * 0.95));

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const baseURL = '/api';
  const token = localStorage.getItem('token');

  // -------------------- Source from the same dashboard data the Build
  // Analytics page uses — no new API calls. dashboardData.recentBuilds is the
  // canonical recent-build feed kept fresh by the auth context.
  const allBuilds = useMemo(() => dashboardData?.recentBuilds || [], [dashboardData]);

  useEffect(() => {
    if (!allBuilds.length) return;
    const errorsOnly: PipelineError[] = allBuilds
      .filter((b: any) => {
        const s = (b.status || b.pipelineStatus || '').toLowerCase();
        return s.includes('fail') || s.includes('cancel') || s.includes('stuck') || s.includes('timeout');
      })
      .map((b: any) => {
        const s = (b.status || '').toLowerCase();
        let errorType: PipelineError['errorType'] = 'failed';
        if (s.includes('stuck')) errorType = 'stuck';
        else if (s.includes('timeout')) errorType = 'timeout';
        else if (s.includes('cancel')) errorType = 'cancelled';
        const description =
          b.failureReason ||
          b.errorMessage ||
          b.phaseStatus ||
          'No detailed diagnostic logs available';
        return {
          // Deterministic id so the detail drawer doesn't 404 and the DataGrid
          // doesn't reshuffle on refresh (mirrors BuildsListComponent). Never
          // Math.random().
          id: String(b.id || b.pipelineId || `${b.composerHost || b.domain || 'unknown'}-${b.dateTimeStamp || b.updatedAt || b.createdAt || 'na'}`),
          pipelineId: String(b.pipelineId || 'N/A'),
          appName: b.composerHost || b.domain || 'Unknown',
          submitter: b.submitter || b.user || 'N/A',
          timestamp: b.dateTimeStamp || b.updatedAt || new Date().toISOString(),
          status: b.pipelineStatus || b.status || 'Failed',
          phase: b.failedPhase || b.phase || 'Unknown',
          errorType,
          platform: b.osSpecification || b.platform || '',
          version: b.version || '',
          pipelineUrl: b.pipelineUrl || '',
          errorDescription: description,
          errorCode: b.errorCode || null,
        };
      });
    setAllPipelineErrors(errorsOnly);
  }, [allBuilds]);

  // Client-side date filter — narrows the table without re-fetching the feed.
  useEffect(() => {
    let filtered = [...allPipelineErrors];
    if (startDate || endDate) {
      filtered = filtered.filter(error => {
        const d = new Date(error.timestamp);
        if (startDate && d < startDate) return false;
        if (endDate) {
          const e = new Date(endDate);
          e.setHours(23, 59, 59, 999);
          if (d > e) return false;
        }
        return true;
      });
    }
    setPipelineErrors(filtered);
  }, [startDate, endDate, allPipelineErrors]);

  // First fetch if the auth context hasn't loaded yet (e.g. landed directly here)
  useEffect(() => {
    if (isActive && (!dashboardData?.recentBuilds || dashboardData.recentBuilds.length === 0)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Periodic refresh while open — reuses the same dashboard endpoint everyone
  // else uses, no new API.
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(fetchDashboardData, 35000 + Math.random() * 5000);
    return () => clearInterval(interval);
  }, [isActive, fetchDashboardData]);

  // -------------------- Search + grouping --------------------
  const filteredErrors = useMemo(() => {
    const q = errorSearch.trim().toLowerCase();
    if (!q) return pipelineErrors;
    return pipelineErrors.filter(e => (
      (e.errorDescription || '').toLowerCase().includes(q) ||
      (e.errorCode || '').toLowerCase().includes(q) ||
      (e.appName || '').toLowerCase().includes(q) ||
      (e.phase || '').toLowerCase().includes(q) ||
      (e.submitter || '').toLowerCase().includes(q) ||
      (e.pipelineId || '').toLowerCase().includes(q)
    ));
  }, [pipelineErrors, errorSearch]);

  const normaliseError = (msg: string): string => {
    if (!msg) return '(no message)';
    return msg
      .replace(/\b\d{4}-\d{2}-\d{2}T[\d:.]+Z?\b/g, '<ts>')
      .replace(/\b[0-9a-f]{7,}\b/gi, '<hash>')
      .replace(/\b\d+\b/g, '<n>')
      .replace(/\/[^\s]+/g, '<path>')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 220);
  };

  const groupedErrors = useMemo(() => {
    const groups = new Map<string, { signature: string; sample: PipelineError; occurrences: PipelineError[] }>();
    for (const e of filteredErrors) {
      const sig = normaliseError(e.errorDescription || '');
      const existing = groups.get(sig);
      if (existing) existing.occurrences.push(e);
      else groups.set(sig, { signature: sig, sample: e, occurrences: [e] });
    }
    return Array.from(groups.values()).sort((a, b) => b.occurrences.length - a.occurrences.length);
  }, [filteredErrors]);

  // -------------------- Drawer --------------------
  const openDrawer = async (error: PipelineError) => {
    setDrawerError(error);
    setDrawerTab(0);
    setDrawerPhases(null);
    setDrawerPhasesError(null);
    setDrawerPhasesLoading(true);
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`/api/builds/${encodeURIComponent(error.id)}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      setDrawerPhases(resp.data?.phases || []);
    } catch (err: any) {
      setDrawerPhasesError(err?.response?.data?.error || 'Failed to load phase details');
    } finally {
      setDrawerPhasesLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerError(null);
    setDrawerPhases(null);
    setDrawerPhasesError(null);
    setDrawerJobs(null);
    setDrawerJobId(null);
    setDrawerTrace('');
    setDrawerTraceMeta(null);
    setDrawerTraceError(null);
  };

  // Fetch the GitLab job trace via the Cloudflare parent-app-worker. The
  // endpoint returns both the trace and the list of jobs on the pipeline so
  // the user can switch between jobs without us re-listing them. Lazy — only
  // fires when the user actually opens the Log tab.
  const fetchTrace = async (jobId?: number) => {
    if (!drawerError) return;
    const pid = drawerError.pipelineId;
    if (!pid || pid.startsWith('NO_PIPELINE_') || pid === 'N/A') {
      setDrawerTraceError('No pipeline ID — this error has no GitLab trace.');
      return;
    }
    setDrawerTraceLoading(true);
    setDrawerTraceError(null);
    const seq = ++traceSeqRef.current;
    try {
      const tok = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (jobId != null) params.append('jobId', String(jobId));
      const qs = params.toString();
      const url = `/api/builds/gitlab/pipelines/${encodeURIComponent(pid)}/trace${qs ? `?${qs}` : ''}`;
      const resp = await axios.get(url, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (seq !== traceSeqRef.current) return; // a newer fetch superseded this one
      setDrawerTrace(resp.data?.trace || '');
      setDrawerTraceMeta({
        truncated: !!resp.data?.truncated,
        totalBytes: resp.data?.totalBytes ?? 0,
        returnedBytes: resp.data?.returnedBytes ?? 0,
        job: resp.data?.job,
      });
      if (Array.isArray(resp.data?.jobs)) setDrawerJobs(resp.data.jobs);
      if (resp.data?.job?.id) setDrawerJobId(resp.data.job.id);
    } catch (err: any) {
      if (seq === traceSeqRef.current) setDrawerTraceError(err?.response?.data?.error || 'Failed to fetch job trace');
    } finally {
      if (seq === traceSeqRef.current) setDrawerTraceLoading(false);
    }
  };

  // First switch into the Log tab triggers the fetch. Subsequent switches
  // back use the cached payload.
  useEffect(() => {
    if (drawerTab === 2 && drawerError && !drawerTrace && !drawerTraceLoading && !drawerTraceError) {
      fetchTrace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerTab, drawerError]);

  // -------------------- CSV export --------------------
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      const response = await axios.get(`${baseURL}/analytics/export-csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `error-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Report exported successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to export report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // -------------------- Table columns --------------------
  const getStatusDisplay = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes('cancel')) return { icon: <Cancel fontSize="small" />, color: 'warning' as const, label: 'Canceled' };
    return { icon: <ErrorIcon fontSize="small" />, color: 'error' as const, label: 'Failed' };
  };

  const columns: GridColDef[] = [
    {
      field: 'pipelineId',
      headerName: 'Pipeline',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const pid = params.row.pipelineId;
        if (pid?.startsWith('NO_PIPELINE_') || pid === 'N/A') return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        return (
          <Typography component="a" href={params.row.pipelineUrl} target="_blank" variant="body2" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}>
            #{pid}
          </Typography>
        );
      },
    },
    {
      field: 'appName',
      headerName: 'App Details',
      width: 240,
      renderCell: (params: GridRenderCellParams) => {
        const platform = (params.row.platform || '').toLowerCase();
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
            <Avatar sx={{ bgcolor: platform === 'ios' ? '#000' : '#3DDC84', width: 32, height: 32, flexShrink: 0 }}>
              {platform === 'ios' ? <Apple sx={{ fontSize: 18 }} /> : <Android sx={{ fontSize: 18 }} />}
            </Avatar>
            <Box sx={{ minWidth: 0, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {params.row.appName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                v{params.row.version || '0.0.0'} • {platform === 'ios' ? 'iOS' : 'Android'}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const display = getStatusDisplay(params.value);
        return <Chip label={display.label} color={display.color} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />;
      },
    },
    {
      field: 'diagnostics',
      headerName: 'Phase & Diagnostics',
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const phase = params.row.phase || 'Unknown';
        const desc = params.row.errorDescription || '';
        const code = params.row.errorCode;
        return (
          <Tooltip title="Click to view full error and open GitLab logs" placement="top-start" arrow enterDelay={400}>
            <Stack
              role="button"
              tabIndex={0}
              onClick={() => openDrawer(params.row as PipelineError)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrawer(params.row as PipelineError); } }}
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.5,
                transition: 'background-color 120ms ease',
                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.06)' },
                '&:focus-visible': { outline: '2px solid #6366f1', outlineOffset: 2 },
              }}
            >
              <Box sx={{
                bgcolor: 'rgba(0,0,0,0.04)',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                minWidth: 100,
                textAlign: 'center',
                border: '1px solid rgba(0,0,0,0.08)',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', color: '#4a5568' }}>
                  {phase.replace(/_/g, ' ')}
                </Typography>
              </Box>
              {code && (
                <Chip
                  label={code}
                  size="small"
                  sx={{
                    height: 20,
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: 'rgba(244, 67, 54, 0.08)',
                    color: '#c62828',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                  }}
                />
              )}
              <Typography variant="caption" sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.2,
                fontSize: '0.75rem',
                flex: 1,
              }}>
                {desc}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    { field: 'submitter', headerName: 'Submitter', width: 160 },
    {
      field: 'timestamp',
      headerName: 'Time',
      width: 130,
      headerAlign: 'right',
      align: 'right',
      renderCell: (p) => (
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {format(new Date(p.value), 'MMM dd, HH:mm')}
        </Typography>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
              Error Logs
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Searchable, groupable view of every pipeline failure with inline log preview and phase timing.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              data-tour="errors-export"
              variant="outlined"
              startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
              onClick={handleExport}
              disabled={isExporting}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1.5 }}>
            <CalendarMonth color="action" />
            <DatePicker label="Start" value={startDate} onChange={setStartDate} sx={{ width: 150 }} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End" value={endDate} onChange={setEndDate} sx={{ width: 150 }} slotProps={{ textField: { size: 'small' } }} />
            {(startDate || endDate) && <Button size="small" onClick={() => { setStartDate(null); setEndDate(null); }}>Clear dates</Button>}

            <Divider orientation="vertical" flexItem />

            <TextField
              data-tour="errors-search"
              size="small"
              placeholder="Search errors, codes, apps, phases…"
              value={errorSearch}
              onChange={(e) => setErrorSearch(e.target.value)}
              sx={{ width: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>
                ),
                endAdornment: errorSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setErrorSearch('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={tableView}
              onChange={(_, v) => v && setTableView(v)}
              sx={{
                height: 40,
                '& .MuiToggleButton-root': {
                  textTransform: 'none', px: 1.5, fontWeight: 600, border: '1px solid #e2e8f0',
                  '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#4338ca', borderColor: '#6366f1', zIndex: 1 },
                },
              }}
            >
              <Tooltip title="Show every failure individually" arrow>
                <ToggleButton value="list"><ViewList sx={{ fontSize: 18, mr: 0.5 }} />List</ToggleButton>
              </Tooltip>
              <Tooltip title="Cluster failures with the same error signature" arrow>
                <ToggleButton value="grouped"><GroupWork sx={{ fontSize: 18, mr: 0.5 }} />Grouped</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>

            {(errorSearch || tableView === 'grouped') && (
              <Typography variant="caption" color="text.secondary">
                {tableView === 'grouped'
                  ? `${groupedErrors.length} pattern${groupedErrors.length !== 1 ? 's' : ''} · ${filteredErrors.length} occurrence${filteredErrors.length !== 1 ? 's' : ''}`
                  : `${filteredErrors.length} of ${pipelineErrors.length} match${filteredErrors.length !== 1 ? '' : 'es'}`}
              </Typography>
            )}
          </Stack>
        </Paper>

        {tableView === 'list' ? (
          <Paper sx={{ ...dataGridContainerSx, height: 650 }}>
            <DataGrid
              rows={filteredErrors}
              columns={columns}
              loading={isLoadingAppData && filteredErrors.length === 0}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              rowHeight={60}
              sx={dataGridSx}
            />
          </Paper>
        ) : (
          <Paper sx={{ ...dataGridContainerSx, p: 0 }}>
            {groupedErrors.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No errors match the current filters.</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {groupedErrors.map((group, idx) => (
                  <GroupedErrorRow key={group.signature || idx} group={group} openDrawer={openDrawer} />
                ))}
              </List>
            )}
          </Paper>
        )}

        <Drawer
          anchor="right"
          open={!!drawerError}
          onClose={closeDrawer}
          slotProps={{
            backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' } },
          }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: effectiveDrawerWidth },
              // While resizing, kill the smooth transition so the drawer
              // tracks the cursor 1:1 instead of trailing behind.
              transition: isResizingRef.current ? 'none' : undefined,
              borderRadius: 0,
              overflow: 'visible', // let the resize handle hang off the edge
            },
          }}
        >
          {drawerError && (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
              {/* Resize handle. A thin 6 px strip pinned to the drawer's
                  left edge. Hover lights it up; drag widens the drawer in
                  real time. Hidden on the xs breakpoint where the drawer
                  fills the viewport anyway. */}
              <Box
                onMouseDown={startDrawerResize}
                aria-label="Resize drawer"
                role="separator"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: -3,
                  width: 6,
                  height: '100%',
                  cursor: 'col-resize',
                  zIndex: 2,
                  bgcolor: 'transparent',
                  display: { xs: 'none', sm: 'block' },
                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' },
                  '&:active': { bgcolor: 'rgba(99, 102, 241, 0.65)' },
                  transition: 'background-color 120ms ease',
                }}
              />
              <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff', flexShrink: 0 }}>
                    <ErrorIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>{drawerError.appName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      v{drawerError.version || '0.0.0'} · {(drawerError.platform || '').toLowerCase() === 'ios' ? 'iOS' : 'Android'} · {format(new Date(drawerError.timestamp), 'MMM dd, HH:mm')}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {/* One-click expand to 95vw / collapse back to the saved
                      drag width. Hidden on xs since the drawer is already
                      full-screen there. */}
                  <Tooltip title={drawerExpanded ? 'Collapse drawer' : 'Expand drawer to full width'} arrow>
                    <IconButton
                      onClick={() => setDrawerExpanded(v => !v)}
                      size="small"
                      sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                      aria-label={drawerExpanded ? 'Collapse drawer' : 'Expand drawer'}
                    >
                      {drawerExpanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={closeDrawer} size="small" aria-label="Close drawer"><CloseIcon /></IconButton>
                </Stack>
              </Box>
              <Divider />
              <Tabs
                value={drawerTab}
                onChange={(_, v) => setDrawerTab(v)}
                sx={{ px: 2, borderBottom: '1px solid #e2e8f0' }}
              >
                <Tab label="Diagnostics" sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label="Phase timing" icon={<Timer sx={{ fontSize: 16 }} />} iconPosition="end" sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label="Log" icon={<Terminal sx={{ fontSize: 16 }} />} iconPosition="end" sx={{ textTransform: 'none', fontWeight: 700 }} />
              </Tabs>

              {drawerTab === 0 && (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Pipeline</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {drawerError.pipelineId && !drawerError.pipelineId.startsWith('NO_PIPELINE_') && drawerError.pipelineId !== 'N/A' ? `#${drawerError.pipelineId}` : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Phase</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        {(drawerError.phase || 'Unknown').replace(/_/g, ' ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Error code</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: drawerError.errorCode ? '#c62828' : 'text.secondary' }}>
                        {drawerError.errorCode || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Submitter</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {drawerError.submitter || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Error message</Typography>
                    <Tooltip title="Copy to clipboard">
                      <IconButton size="small" onClick={() => {
                        navigator.clipboard?.writeText(drawerError.errorDescription || '')
                          .then(() => showSnackbar('Error message copied.', 'success'))
                          .catch(() => showSnackbar('Copy failed.', 'error'));
                      }}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{
                    bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2, p: 2,
                    fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.5,
                    maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {drawerError.errorDescription || 'No detailed diagnostic available.'}
                  </Box>
                </Box>
              )}

              {drawerTab === 2 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 3, pb: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Job</Typography>
                    {drawerJobs && drawerJobs.length > 0 ? (
                      <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                        <Select
                          value={drawerJobId ?? ''}
                          onChange={(e) => {
                            const id = Number(e.target.value);
                            setDrawerJobId(id);
                            fetchTrace(id);
                          }}
                          sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}
                        >
                          {drawerJobs.map((j) => (
                            <MenuItem key={j.id} value={j.id} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>{j.name}</Typography>
                                <Chip
                                  size="small"
                                  label={j.status}
                                  sx={{
                                    height: 18, fontSize: '0.65rem', fontWeight: 700, textTransform: 'capitalize',
                                    bgcolor: /success/i.test(j.status) ? 'rgba(22, 163, 74, 0.12)'
                                      : /fail/i.test(j.status) ? 'rgba(220, 38, 38, 0.12)'
                                      : /cancel/i.test(j.status) ? 'rgba(245, 158, 11, 0.12)'
                                      : 'rgba(99, 102, 241, 0.12)',
                                    color: /success/i.test(j.status) ? '#15803d'
                                      : /fail/i.test(j.status) ? '#b91c1c'
                                      : /cancel/i.test(j.status) ? '#92400e'
                                      : '#4338ca',
                                  }}
                                />
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {drawerTraceLoading ? 'Loading…' : 'No jobs loaded yet'}
                      </Typography>
                    )}
                    <Tooltip title="Refresh log">
                      <span>
                        <IconButton size="small" onClick={() => fetchTrace(drawerJobId ?? undefined)} disabled={drawerTraceLoading}>
                          <Refresh fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Copy to clipboard">
                      <span>
                        <IconButton size="small" disabled={!drawerTrace} onClick={() => {
                          navigator.clipboard?.writeText(drawerTrace)
                            .then(() => showSnackbar('Log copied.', 'success'))
                            .catch(() => showSnackbar('Copy failed.', 'error'));
                        }}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Download .log">
                      <span>
                        <IconButton size="small" disabled={!drawerTrace} onClick={() => {
                          const blob = new Blob([drawerTrace], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `pipeline-${drawerError?.pipelineId || 'unknown'}-job-${drawerTraceMeta?.job?.id || 'job'}.log`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}>
                          <Download fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  {drawerTraceMeta?.truncated && (
                    <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                      Showing the last {(drawerTraceMeta.returnedBytes / 1024).toFixed(0)} KB of a {(drawerTraceMeta.totalBytes / 1024).toFixed(0)} KB log. Use <strong>Download .log</strong> for the full trace from GitLab.
                    </Alert>
                  )}

                  {drawerTraceError && (
                    <Alert severity="error" sx={{ mb: 1 }}>{drawerTraceError}</Alert>
                  )}

                  <Box sx={{
                    flex: 1, minHeight: 240,
                    bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2, p: 2,
                    fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.45,
                    overflow: 'auto', whiteSpace: 'pre', wordBreak: 'normal',
                  }}>
                    {drawerTraceLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={24} sx={{ color: '#94a3b8' }} />
                      </Box>
                    ) : drawerTrace ? drawerTrace : (
                      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                        {drawerTraceError ? 'Could not load trace.' : 'No log content.'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {drawerTab === 1 && (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  {drawerPhasesLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                  {drawerPhasesError && !drawerPhasesLoading && (
                    <Alert severity="error" sx={{ mb: 2 }}>{drawerPhasesError}</Alert>
                  )}
                  {!drawerPhasesLoading && !drawerPhasesError && drawerPhases && drawerPhases.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No phase data captured for this build.</Typography>
                  )}
                  {!drawerPhasesLoading && drawerPhases && drawerPhases.length > 0 && (() => {
                    const completed = drawerPhases.filter((p: any) => p.startedAt && p.finishedAt);
                    const maxDuration = completed.reduce((m, p) => {
                      const dur = new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime();
                      return Math.max(m, dur);
                    }, 0);
                    const totalDuration = completed.reduce((sum, p) => sum + (new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime()), 0);
                    return (
                      <Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                          <Chip label={`Total: ${formatDuration(totalDuration)}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.12)', color: '#4338ca' }} />
                          {maxDuration > 0 && (
                            <Chip label={`Slowest phase: ${formatDuration(maxDuration)}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#92400e' }} />
                          )}
                        </Stack>
                        <List dense disablePadding>
                          {drawerPhases.map((p: any, i: number) => {
                            const dur = p.startedAt && p.finishedAt
                              ? new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime()
                              : null;
                            const pct = dur && maxDuration > 0 ? (dur / maxDuration) * 100 : 0;
                            const ps = (p.status || '').toLowerCase();
                            const phaseColor = /success/.test(ps) ? '#16a34a' : /fail/.test(ps) ? '#dc2626' : /cancel/.test(ps) ? '#f59e0b' : /running/.test(ps) ? '#3b82f6' : '#94a3b8';
                            const isSlowest = dur === maxDuration && maxDuration > 0;
                            return (
                              <ListItem
                                key={`${p.id || i}-${p.phase}`}
                                disableGutters
                                sx={{
                                  flexDirection: 'column', alignItems: 'stretch', mb: 1,
                                  border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5,
                                  ...(isSlowest && { borderColor: 'rgba(245, 158, 11, 0.5)', backgroundColor: 'rgba(245, 158, 11, 0.06)' }),
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: phaseColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                                    {(p.sequence ?? i) + 1}
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, textTransform: 'uppercase' }}>
                                    {(p.phase || 'unknown').replace(/_/g, ' ')}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={ps || 'pending'}
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${phaseColor}1a`, color: phaseColor, textTransform: 'capitalize' }}
                                  />
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', minWidth: 56, textAlign: 'right' }}>
                                    {dur !== null ? formatDuration(dur) : '—'}
                                  </Typography>
                                </Stack>
                                {dur !== null && (
                                  <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{
                                      height: 6, borderRadius: 1, bgcolor: '#f1f5f9',
                                      '& .MuiLinearProgress-bar': { bgcolor: phaseColor },
                                    }}
                                  />
                                )}
                                {p.errorMessage && (
                                  <Collapse in={true}>
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(220, 38, 38, 0.06)', borderRadius: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: '#991b1b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {p.errorMessage}
                                    </Box>
                                  </Collapse>
                                )}
                                {p.gitlabJobUrl && (
                                  <Button
                                    size="small"
                                    href={p.gitlabJobUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                    sx={{ alignSelf: 'flex-start', mt: 0.75, textTransform: 'none', fontWeight: 600 }}
                                  >
                                    GitLab job
                                  </Button>
                                )}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              <Divider />
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={closeDrawer} color="inherit">Close</Button>
                <Button
                  variant="contained"
                  startIcon={<OpenInNew />}
                  disabled={!drawerError.pipelineUrl}
                  onClick={() => drawerError.pipelineUrl && window.open(drawerError.pipelineUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open GitLab Job
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

// -------------------- Helpers + subcomponents --------------------

function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

const GroupedErrorRow: React.FC<{
  group: { signature: string; sample: PipelineError; occurrences: PipelineError[] };
  openDrawer: (e: PipelineError) => void;
}> = ({ group, openDrawer }) => {
  const [open, setOpen] = useState(false);
  const { sample, occurrences } = group;
  const recent = occurrences.slice().sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return (
    <>
      <ListItem
        onClick={() => setOpen(v => !v)}
        sx={{
          cursor: 'pointer',
          borderBottom: '1px solid #f1f5f9',
          '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.04)' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Chip
            label={occurrences.length}
            size="small"
            sx={{ fontWeight: 800, bgcolor: occurrences.length > 5 ? '#dc2626' : 'rgba(220, 38, 38, 0.12)', color: occurrences.length > 5 ? '#fff' : '#b91c1c' }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {(sample.errorDescription || '(no message)').slice(0, 240)}
            </Typography>
          }
          secondary={
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
              <Chip size="small" label={(sample.phase || 'Unknown').replace(/_/g, ' ')} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(15, 23, 42, 0.06)' }} />
              {sample.errorCode && <Chip size="small" label={sample.errorCode} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace', bgcolor: 'rgba(244, 67, 54, 0.08)', color: '#c62828' }} />}
              <Typography variant="caption" color="text.secondary">
                Latest: {format(new Date(recent[0].timestamp), 'MMM dd, HH:mm')} · {recent[0].appName}
              </Typography>
            </Stack>
          }
        />
      </ListItem>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 7, pr: 2, py: 1, bgcolor: '#fafbfc' }}>
          <List dense disablePadding>
            {recent.slice(0, 12).map(o => (
              <ListItem
                key={o.id}
                onClick={() => openDrawer(o)}
                sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.06)' } }}
                secondaryAction={
                  <Tooltip title="Open in drawer" arrow>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDrawer(o); }}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{o.appName}</Typography>
                      <Typography variant="caption" color="text.secondary">v{o.version || '0.0.0'}</Typography>
                      <Chip size="small" label={(o.platform || '').toLowerCase() === 'ios' ? 'iOS' : 'Android'} sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(o.timestamp), 'MMM dd, HH:mm')} · {o.submitter || 'N/A'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
            {recent.length > 12 && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                …and {recent.length - 12} more
              </Typography>
            )}
          </List>
        </Box>
      </Collapse>
    </>
  );
};

export default ErrorLogsComponent;
